// src/utils/specialCategories.ts

export const SPECIAL_CATEGORIES = [
    { id: "mobilite", name: "Mobilité / Déplacements", icon: "🏃" },
    { id: "puissance", name: "Puissance / Force", icon: "💪" },
    { id: "construction", name: "Construction / Précision", icon: "🏗️" },
    { id: "agressivite", name: "Agressivité", icon: "⚔️" }
  ] as const;

export type SpecialCategoryId = (typeof SPECIAL_CATEGORIES)[number]["id"];

export type SpecialAssignment = Record<SpecialCategoryId, boolean>;

const EMPTY_SPECIAL_ASSIGNMENT: SpecialAssignment = {
  mobilite: false,
  puissance: false,
  construction: false,
  agressivite: false,
};

const normalizeCategoryName = (name: string) => name.trim().toLowerCase();

const SPECIAL_CATEGORY_NAME_SET = new Set(
  SPECIAL_CATEGORIES.map((category) => normalizeCategoryName(category.name))
);
  
  // ============================================================
  // CONFIGURATION PAR DÉFAUT (assignation exercice → dimensions)
  // ============================================================
  
  export const DEFAULT_SPECIAL_ASSIGNMENTS: {
    [exerciseId: string]: {
      mobilite: boolean;
      puissance: boolean;
      construction: boolean;
      agressivite: boolean;
    };
  } = {
    "7": { construction: true, mobilite: false, puissance: false, agressivite: false },
    "8": { mobilite: true, construction: false, puissance: false, agressivite: false },
    "10": { agressivite: true, puissance: true, construction: false, mobilite: false },
    "11": { mobilite: true, construction: true, puissance: false, agressivite: false },
    "12": { agressivite: true, puissance: true, construction: false, mobilite: false },
    "13": { agressivite: true, construction: false, mobilite: false, puissance: false },
    "15": { mobilite: true, agressivite: true, construction: false, puissance: false },
    "17": { mobilite: true, construction: false, puissance: false, agressivite: false },
    "18": { agressivite: true, puissance: true, construction: false, mobilite: false },
    "19": { construction: true, mobilite: false, puissance: false, agressivite: false },
    "32": { mobilite: true, construction: false, puissance: false, agressivite: false }
  };

export function normalizeSpecialAssignments(assignments: unknown) {
  const normalized: Record<string, SpecialAssignment> = {};

  Object.entries(DEFAULT_SPECIAL_ASSIGNMENTS).forEach(([exerciseId, entry]) => {
    normalized[exerciseId] = {
      ...EMPTY_SPECIAL_ASSIGNMENT,
      ...entry,
    };
  });

  if (!assignments || typeof assignments !== "object") {
    return normalized;
  }

  Object.entries(assignments as Record<string, Partial<SpecialAssignment>>).forEach(
    ([exerciseId, entry]) => {
      normalized[exerciseId] = {
        ...EMPTY_SPECIAL_ASSIGNMENT,
        ...normalized[exerciseId],
        ...(entry || {}),
      };
    }
  );

  return normalized;
}

export function isSpecialCategoryName(name?: string | null) {
  if (!name) return false;
  return SPECIAL_CATEGORY_NAME_SET.has(normalizeCategoryName(name));
}

export function filterOutSpecialCategories<T extends { name?: string | null }>(
  categories: T[]
) {
  return categories.filter((category) => !isSpecialCategoryName(category.name));
}
  
  // ============================================================
  // UTILITAIRES COMMUNS
  // ============================================================
  
  // Liste des exercices appartenant à une catégorie spéciale
  export function getExercisesForSpecialCategory(
    assignments: Record<string, Record<string, boolean>>,
    specialId: string
  ): number[] {
    return Object.entries(assignments)
      .filter(([_, m]) => Boolean(m[specialId]))
      .map(([exId]) => Number(exId));
  }

export function countExercisesForSpecialCategory(
  assignments: Record<string, Record<string, boolean>>,
  specialId: string
) {
  return getExercisesForSpecialCategory(assignments, specialId).length;
}
  
  export function loadSpecialAssignments() {
    try {
      const saved = localStorage.getItem("specialAssignments");
      if (saved) return normalizeSpecialAssignments(JSON.parse(saved));
    } catch (e) {
      console.warn("Erreur parsing specialAssignments:", e);
    }
    return normalizeSpecialAssignments(DEFAULT_SPECIAL_ASSIGNMENTS);
  }

  export function saveSpecialAssignments(assignments: any) {
    localStorage.setItem(
      "specialAssignments",
      JSON.stringify(normalizeSpecialAssignments(assignments))
    );
  }

  // Taux de réussite d'une évaluation pour un exercice donné
  export function computeSuccessRate(evaluation: any, exercise: any): number {
    try {
      const scores: number[] = JSON.parse(evaluation.details || "[]");
      const totalScore = scores.reduce((a, b) => a + b, 0);
      const maxPossible = exercise.max_points * exercise.repetitions;
      return maxPossible > 0 ? (totalScore / maxPossible) * 100 : 0;
    } catch {
      return 0;
    }
  }
  
  // Calcul d'un score spécial pondéré pour un joueur
  export function computeSpecialRatesForPlayer(
    playerId: number,
    evaluations: any[],
    exercises: any[],
    assignments: any,
    specialId: string,
    getCoef: (title: string) => number
  ) {
    const exerciseIds = getExercisesForSpecialCategory(assignments, specialId);
  
    const evals = evaluations.filter(
      ev => ev.player_id === playerId && exerciseIds.includes(ev.exercise_id)
    );
  
    let total = 0;
    let coefSum = 0;
    let count = 0;
  
    evals.forEach(ev => {
      const ex = exercises.find(e => e.id === ev.exercise_id);
      if (!ex) return;
  
      const rate = computeSuccessRate(ev, ex);
      const coef = getCoef(ex.title);
  
      total += rate * coef;
      coefSum += coef;
      count++;
    });
  
    return {
      rate: coefSum ? Number((total / coefSum).toFixed(2)) : 0,
      count
    };
  }
  